import { jsx as _jsx } from "react/jsx-runtime";
/**
 * IconBtn — icon-only action button primitive.
 *
 * Sketched in Claude Design 2026-05-10 as part of the dashboard surface.
 * Rationale: a 44×44px tap target (WCAG 2.5.5 AA / CLAUDE.md rule 7) is built
 * INTO the primitive — the `<button>` is a transparent 44px hit + focus area
 * wrapping a 40×40px visual circle, so the rendered density is unchanged while
 * every consumer meets the touch-target floor for free (SCRUM-385, superseding
 * the old "consumer pads surroundings to ≥44px" contract). Variants mirror
 * Button hover/focus shadows. aria-label is required (axe button-name rule
 * enforced in stories).
 *
 * Absolutely-positioned children (e.g. an unread dot) render inside the 40px
 * visual span — it is the `relative` box — so they hug the visible circle
 * rather than the transparent tap padding.
 */
import * as React from 'react';
import { cn } from './cn.js';
// The <button> is the 44×44px tap + focus target (WCAG 2.5.5 AA). It is
// transparent and padding-free; the visible surface is the inner circle.
// `group` lets that circle react to the button's hover / focus-visible state.
// `min-w-touch`/`min-h-touch` (the design-tokens 44px floor) + `shrink-0`
// make 44px a HARD floor, not just a preferred size: as a flex item the button
// would otherwise shrink toward its 40px child in a cramped row (Simon #370).
const BUTTON_CLASSES = [
    'group',
    'inline-flex',
    'w-11',
    'h-11',
    'min-w-touch',
    'min-h-touch',
    'shrink-0',
    'items-center',
    'justify-center',
    'p-0',
    'bg-transparent',
    'border-none',
    'cursor-pointer',
    'focus-visible:outline-none',
    'disabled:pointer-events-none',
    'disabled:opacity-50',
].join(' ');
// The 40×40px visible circle. Hover background + focus ring live here (driven
// by the button's state via `group-*`) so the ring hugs the visual rather than
// the larger tap area — identical to the pre-SCRUM-385 rendered appearance.
// `relative` makes it the positioning context for badge/dot children.
const VISUAL_CLASSES = [
    'relative',
    'inline-flex',
    'w-10',
    'h-10',
    'items-center',
    'justify-center',
    'rounded-full',
    'text-[var(--fg-secondary)]',
    'transition-[background]',
    'duration-[var(--dur-fast)]',
    'group-hover:bg-[var(--bg-hover)]',
    'group-focus-visible:ring-2',
    'group-focus-visible:ring-brand-500',
    'group-focus-visible:ring-offset-2',
].join(' ');
export const IconBtn = React.forwardRef(function IconBtn({ label, children, className, type, ...rest }, ref) {
    return (_jsx("button", { ref: ref, type: type ?? 'button', "aria-label": label, className: cn(BUTTON_CLASSES, className), ...rest, children: _jsx("span", { className: VISUAL_CLASSES, children: children }) }));
});
IconBtn.displayName = 'IconBtn';
//# sourceMappingURL=IconBtn.js.map